import { DEFAULT_TABLE_AUTO_REFRESH, TableAutoRefreshMode, resolveTableAutoRefreshMode } from '@frontend/repository';
import { ToastUtil } from '@frontend/toast-utils';
import { TableViewClient } from '@frontend/user/api';
import type { ITableView, TableViewAutoRefresh } from '@frontend/user/types';
import type { VisibilityState } from '@tanstack/react-table';
import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { IntlShape, useIntl } from 'react-intl';

import { mergeSavedVisibleColumnOrder } from './table-view-column-order';

interface UseTableViewPreferencesProps {
    tableViewId?: string;
    defaultColumnIds: string[];
    hideColumns?: string[];
}

interface UseTableViewPreferencesResult {
    columnVisibility: VisibilityState;
    setColumnVisibility: Dispatch<SetStateAction<VisibilityState>>;
    columnOrder: string[];
    setColumnOrder: Dispatch<SetStateAction<string[]>>;
    visibleColumnIds: string[];
    autoRefresh: TableAutoRefreshMode;
    setAutoRefresh: (mode: TableAutoRefreshMode) => void;
    isTableViewCustomized: boolean;
    resetTableView: () => void;
}

let didShowTableViewLoadError = false;

export const useTableViewPreferences = (props: UseTableViewPreferencesProps): UseTableViewPreferencesResult => {
    const intl = useIntl();
    const { tableViewId, defaultColumnIds, hideColumns } = props;
    // Inline arrays like hideColumns={[id]} recreate every render — compare by content, read latest via refs.
    const defaultColumnIdsKey = defaultColumnIds.join('\u001f');
    const hideColumnsKey = (hideColumns ?? []).join('\u001f');
    const defaultColumnIdsRef = useRef(defaultColumnIds);
    const hideColumnsRef = useRef(hideColumns);
    defaultColumnIdsRef.current = defaultColumnIds;
    hideColumnsRef.current = hideColumns;

    const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
    const [columnOrder, setColumnOrder] = useState<string[]>(defaultColumnIds);
    const [autoRefresh, setAutoRefreshState] = useState<TableAutoRefreshMode>(DEFAULT_TABLE_AUTO_REFRESH);
    const [tableView, setTableView] = useState<ITableView | null>(null);
    const [tableViewLoaded, setTableViewLoaded] = useState(false);
    const tableViewRef = useRef<ITableView | null>(null);
    const lastPersistedSnapshotRef = useRef<string | null>(null);
    const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const saveQueueRef = useRef<Promise<ITableView | null>>(Promise.resolve(null));
    const isMountedRef = useRef(true);
    const operationVersionRef = useRef(0);
    const activeTableViewIdRef = useRef(tableViewId);

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
            if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
        };
    }, []);

    useEffect(() => {
        activeTableViewIdRef.current = tableViewId;
    }, [tableViewId]);

    useEffect(() => {
        tableViewRef.current = tableView;
    }, [tableView]);

    useEffect(() => {
        if (!tableViewId) {
            operationVersionRef.current += 1;
            tableViewRef.current = null;
            setTableView(null);
            setAutoRefreshState(DEFAULT_TABLE_AUTO_REFRESH);
            setTableViewLoaded(true);
            return;
        }

        let isMounted = true;
        const loadVersion = operationVersionRef.current + 1;
        operationVersionRef.current = loadVersion;
        tableViewRef.current = null;
        setTableView(null);
        setTableViewLoaded(false);

        TableViewClient.fetchMyTableView(tableViewId)
            .then((nextTableView) => {
                if (!isMounted || loadVersion !== operationVersionRef.current) return;
                setTableView(nextTableView);
            })
            .catch(() => {
                if (!isMounted || loadVersion !== operationVersionRef.current) return;
                setTableView(null);
                showLoadErrorOnce(intl);
            })
            .finally(() => {
                if (isMounted && loadVersion === operationVersionRef.current) setTableViewLoaded(true);
            });

        return () => {
            isMounted = false;
        };
    }, [intl, tableViewId]);

    useEffect(() => {
        if (!tableViewLoaded) return;

        const columnIds = defaultColumnIdsRef.current;
        const hidden = hideColumnsRef.current;
        const visibleFromTableView = getSavedVisibleColumnIds(tableView?.columns, columnIds);
        const nextVisibleColumnIds =
            visibleFromTableView && visibleFromTableView.length > 0 ? visibleFromTableView : getDefaultVisibleColumnIds(columnIds, hidden);
        const nextColumnOrder =
            visibleFromTableView && visibleFromTableView.length > 0 ? mergeSavedVisibleColumnOrder(columnIds, visibleFromTableView) : columnIds;
        const visibleColumnSet = new Set(nextVisibleColumnIds);
        const nextAutoRefresh = resolveTableAutoRefreshMode(tableView?.auto_refresh);

        setColumnOrder(nextColumnOrder);
        setColumnVisibility(
            columnIds.reduce<VisibilityState>((acc, id) => {
                acc[id] = visibleColumnSet.has(id);
                return acc;
            }, {})
        );
        setAutoRefreshState(nextAutoRefresh);
        lastPersistedSnapshotRef.current = serializePreferences(nextVisibleColumnIds, nextAutoRefresh);
    }, [tableViewLoaded, tableView?.id, tableView?.columns, tableView?.auto_refresh, defaultColumnIdsKey, hideColumnsKey]);

    const visibleColumnIds = useMemo(
        () => columnOrder.filter((id) => defaultColumnIdsRef.current.includes(id) && columnVisibility[id] !== false),
        [columnOrder, columnVisibility, defaultColumnIdsKey]
    );

    const isTableViewCustomized = useMemo(() => {
        if (!tableViewLoaded) return false;

        const defaultVisibleColumnIds = getDefaultVisibleColumnIds(defaultColumnIdsRef.current, hideColumnsRef.current);
        const columnsCustomized = !areColumnOrdersEqual(visibleColumnIds, defaultVisibleColumnIds);
        const autoRefreshCustomized = autoRefresh !== DEFAULT_TABLE_AUTO_REFRESH;
        return columnsCustomized || autoRefreshCustomized;
    }, [autoRefresh, defaultColumnIdsKey, hideColumnsKey, tableViewLoaded, visibleColumnIds]);

    const persistTableView = useCallback(
        (columns: string[], nextAutoRefresh: TableAutoRefreshMode) => {
            if (!tableViewId || columns.length === 0) return;

            const snapshot = serializePreferences(columns, nextAutoRefresh);
            if (lastPersistedSnapshotRef.current === snapshot) return;
            if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);

            const requestedTableViewId = tableViewId;
            const saveVersion = operationVersionRef.current + 1;
            operationVersionRef.current = saveVersion;

            saveTimeoutRef.current = setTimeout(() => {
                saveQueueRef.current = saveQueueRef.current
                    .catch(() => null)
                    .then(async () => {
                        if (saveVersion !== operationVersionRef.current) return null;

                        const payload = { columns, auto_refresh: nextAutoRefresh as TableViewAutoRefresh };
                        const currentTableView = tableViewRef.current?.table_id === requestedTableViewId ? tableViewRef.current : null;
                        const nextTableView = currentTableView
                            ? await TableViewClient.patchMyTableView(currentTableView.id, payload)
                            : await TableViewClient.postMyTableView({ table_id: requestedTableViewId, ...payload });

                        const isCurrentTableView = activeTableViewIdRef.current === requestedTableViewId && nextTableView.table_id === requestedTableViewId;
                        if (isCurrentTableView) tableViewRef.current = nextTableView;
                        if (isCurrentTableView && isMountedRef.current && saveVersion === operationVersionRef.current) {
                            lastPersistedSnapshotRef.current = snapshot;
                            setTableView(nextTableView);
                        }
                        return nextTableView;
                    })
                    .catch(() => {
                        if (saveVersion === operationVersionRef.current) {
                            lastPersistedSnapshotRef.current = null;
                            showSaveError(intl);
                        }
                        return null;
                    });
            }, 500);
        },
        [intl, tableViewId]
    );

    useEffect(() => {
        if (!tableViewLoaded || !tableViewId || Object.keys(columnVisibility).length === 0 || columnOrder.length === 0) return;
        persistTableView(visibleColumnIds, autoRefresh);
    }, [autoRefresh, columnOrder, columnVisibility, persistTableView, tableViewId, tableViewLoaded, visibleColumnIds]);

    const setAutoRefresh = useCallback((mode: TableAutoRefreshMode) => {
        setAutoRefreshState(mode);
    }, []);

    const resetTableView = useCallback(() => {
        const nextVisibleColumnIds = getDefaultVisibleColumnIds(defaultColumnIds, hideColumns);
        const resetVersion = operationVersionRef.current + 1;
        operationVersionRef.current = resetVersion;

        setColumnOrder(defaultColumnIds);
        setColumnVisibility(
            defaultColumnIds.reduce<VisibilityState>((acc, id) => {
                acc[id] = nextVisibleColumnIds.includes(id);
                return acc;
            }, {})
        );
        setAutoRefreshState(DEFAULT_TABLE_AUTO_REFRESH);
        lastPersistedSnapshotRef.current = serializePreferences(nextVisibleColumnIds, DEFAULT_TABLE_AUTO_REFRESH);

        if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);

        saveQueueRef.current = saveQueueRef.current
            .catch(() => null)
            .then(async () => {
                const currentTableView = tableViewRef.current;
                if (!currentTableView || currentTableView.table_id !== tableViewId) return null;

                await TableViewClient.deleteMyTableView(currentTableView.id);
                tableViewRef.current = null;

                if (resetVersion === operationVersionRef.current) {
                    if (isMountedRef.current) setTableView(null);
                }
                return null;
            })
            .catch(() => {
                if (resetVersion === operationVersionRef.current) showResetError(intl);
                return null;
            });
    }, [defaultColumnIds, hideColumns, intl, tableViewId]);

    return {
        columnVisibility,
        setColumnVisibility,
        columnOrder,
        setColumnOrder,
        visibleColumnIds,
        autoRefresh,
        setAutoRefresh,
        isTableViewCustomized,
        resetTableView
    };
};

const getDefaultVisibleColumnIds = (columnIds: string[], hideColumns?: string[]): string[] => columnIds.filter((id) => !hideColumns?.includes(id));

const getSavedVisibleColumnIds = (columns: string[] | undefined, defaultColumnIds: string[]): string[] | null => {
    if (!columns) return null;

    const defaultColumnSet = new Set(defaultColumnIds);
    const seenColumnIds = new Set<string>();

    return columns.filter((id) => {
        if (!defaultColumnSet.has(id) || seenColumnIds.has(id)) return false;
        seenColumnIds.add(id);
        return true;
    });
};

const serializePreferences = (columns: string[], autoRefresh: TableAutoRefreshMode): string => `${columns.join('\u001f')}\u001e${autoRefresh}`;

const areColumnOrdersEqual = (first: string[], second: string[]): boolean => first.length === second.length && first.every((id, index) => id === second[index]);

const showLoadErrorOnce = (intl: IntlShape): void => {
    if (didShowTableViewLoadError) return;
    didShowTableViewLoadError = true;

    ToastUtil.error(
        intl.formatMessage({
            id: 'reactive-table.options.load-error.title',
            description: 'Toast title shown when table view settings cannot be loaded',
            defaultMessage: 'Could not load table options'
        }),
        intl.formatMessage({
            id: 'reactive-table.options.load-error.message',
            description: 'Toast message shown when table view settings cannot be loaded',
            defaultMessage: 'The default table layout is being used.'
        })
    );
};

const showSaveError = (intl: IntlShape): void => {
    ToastUtil.error(
        intl.formatMessage({
            id: 'reactive-table.options.save-error.title',
            description: 'Toast title shown when table view settings cannot be saved',
            defaultMessage: 'Could not save table options'
        }),
        intl.formatMessage({
            id: 'reactive-table.options.save-error.message',
            description: 'Toast message shown when table view settings cannot be saved',
            defaultMessage: 'Your table layout may not be restored next time.'
        })
    );
};

const showResetError = (intl: IntlShape): void => {
    ToastUtil.error(
        intl.formatMessage({
            id: 'reactive-table.options.reset-error.title',
            description: 'Toast title shown when table view settings cannot be reset',
            defaultMessage: 'Could not reset table options'
        }),
        intl.formatMessage({
            id: 'reactive-table.options.reset-error.message',
            description: 'Toast message shown when table view settings cannot be reset',
            defaultMessage: 'The saved table layout may still be used next time.'
        })
    );
};
